<template>
    <CompassDialog
        :visible.sync="showModal"
        size="small"
        :title="$t.__('popups.BackupCodes2FA.backup_codes')"
        class="backupCodesModal"
        @closed="close"
    >
        <template #default>
            <div class="dialog__default">
                <p class="mb-2 typographies-paragraph-medium-regular">
                    {{
                        $l10n.__(
                            "Ces codes vous permettent d'accéder à votre compte si vous n'avez temporairement pas accès à votre appareil ou que vous le perdez alors que la double authentification est active.",
                        )
                    }}
                </p>

                <div class="typographies-paragraph-medium-bold">{{ unusedBackupCodes }}</div>

                <p class="backupCodesModal__singleuse mb-2 typographies-paragraph-medium-regular">
                    {{
                        $l10n.__(
                            'Chaque code est à usage unique, vous pouvez en générer des nouveaux si vous les avez tous utilisés.',
                        )
                    }}
                </p>

                <CompassPlainButton
                    :label="$t.__('popups.BackupCodes2FA.generate_news_codes')"
                    leftIcon="revert"
                    :disabled="loading"
                    @click="generateNewCodes"
                />

                <div class="backupCodesModal__list">
                    <circular-loader v-if="loading" class="backupCodesModal__loader" />
                    <ul :class="{ 'is-loading': loading }">
                        <li
                            v-for="backupCode in backupCodes"
                            :key="backupCode.id"
                            :class="{ 'is-used': backupCode.used_at !== null }"
                        >
                            {{ backupCode.code }}
                        </li>
                    </ul>
                </div>

                <div class="backupCodesModal__date">
                    {{ $l10n.__('Codes générés le') }} {{ generationDate }}
                </div>

                <div class="backupCodesModal__actions">
                    <CompassButton
                        :label="$t.__('popups.BackupCodes2FA.print')"
                        type="secondary"
                        :disabled="loading"
                        @click="handlePrint"
                    />
                    <CompassButton
                        :label="$t.__('popups.BackupCodes2FA.download_pdf')"
                        type="secondary"
                        :disabled="loading"
                        class="ml-3"
                        @click="handleDownload"
                    />
                </div>
            </div>
        </template>
        <template #footer>
            <div class="dialog__footer">
                <CompassButton
                    :label="$t.__('common.actions.close')"
                    type="primary"
                    :disabled="loading"
                    @click="close"
                />
            </div>
        </template>
    </CompassDialog>
</template>

<script>
import CircularLoader from 'vcomponents/utilities/circularLoader/CircularLoader.vue';
import createPdf from 'Services/CreatePdf/CreatePdf';

export default {
    components: {
        CircularLoader,
    },
    props: {
        backupCodes: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            showModal: true,
            loading: false,
        };
    },
    computed: {
        generationDate() {
            return moment(this.backupCodes[0].createdAt).format('LL');
        },
        unusedBackupCodes() {
            const total = this.backupCodes.filter((item) => !item.used_at).length;
            return this.$l10n.n__(
                '%s code de secours restant',
                '%s codes de secours restants',
                total,
                total,
            );
        },
        pdfText() {
            return {
                header: this.$l10n.__('Double authentification - Codes de secours'),
                singleUse: this.$l10n.__(
                    'Ces codes sont strictement personnels. Conservez-les en lieu sûr, de préférence avec vous.',
                ),
                generationDate: this.$l10n.__('Ces codes ont été générés le'),
                attention: this.$l10n.__(
                    'Chaque code de secours est à usage unique, rayez-le une fois utilisé.',
                ),
            };
        },
    },
    watch: {
        backupCodes(newValues, oldValues) {
            // keep the old codes hidden until they have been updated
            if (newValues !== oldValues) {
                this.loading = false;
            }
        },
    },
    methods: {
        close() {
            this.$emit('close');
        },
        generateNewCodes() {
            this.loading = true;
            ApiClient.post('/settings/two-factor-authentication/user/backup-codes', {}, {})
                .then((response) => {
                    if (response.status === 201) {
                        EventBus.emit('2fa:updateBackupCodes');
                    }
                })
                .catch((error) => {
                    Push.error(error.message);
                    this.loading = false;
                });
        },
        handlePrint() {
            createPdf(this.backupCodes, this.pdfText);
        },
        handleDownload() {
            createPdf(this.backupCodes, this.pdfText, true);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~@sellsy/styleguide/src/_variables';

.backupCodesModal {
    .dialog {
        &__default {
            padding-bottom: 24px;
        }

        &__footer {
            display: flex;
            justify-content: flex-end;
            padding: 0 32px 24px 32px;
        }
    }

    &__singleuse {
        color: $gray-60;
    }

    &__list {
        background: $gray-0;
        border-radius: 4px;
        color: $gray-100;
        font-size: 17px;
        font-weight: 600;
        margin: 16px 0 8px;
        padding: 24px;
        position: relative;

        ul {
            display: grid;
            grid-auto-flow: column;
            grid-template-columns: 1fr 1fr;
            grid-template-rows: repeat(5, min-content);
            list-style: none;
            margin: 0;
            padding: 0;

            &.is-loading {
                visibility: hidden;
            }
        }

        li {
            line-height: 24px;
            padding-left: 20px;

            &:nth-child(n + 6) {
                border-left: 1px solid $gray-40;
                padding-left: 40px;
            }

            &.is-used {
                color: $gray-60;
                text-decoration: line-through;
            }
        }
    }

    &__loader {
        left: 50%;
        margin: -10px 0 0 -10px;
        position: absolute;
        top: 50%;
    }

    &__date {
        color: $gray-60;
        font-size: 12px;
        font-weight: 600;
        line-height: 16px;
        margin: 0 0 16px;
        text-align: center;
    }

    &__actions {
        display: flex;
        margin: 16px 0 0;

        .btn {
            margin: 0 8px 0 0;
        }
    }
}
</style>
